import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { expireShieldSession, refreshShieldVisionAccessToken } from '@/app/auth/shield-token-source'
import { getVisionAccessToken } from '@/app/auth/token-source'
import { useTenantStore } from '@/app/state/tenant-store'
import { createVisionClient, type VisionClient } from '@/lib/vision/client'
import { withBulkDemoData } from '@/lib/vision/bulk-demo-client'
import { BULK_UI_DEMO, JIT_UI_DEMO, USING_SHIELD_AUTH } from '@/lib/env'

// Exported for tests only — production code should read through
// `useVisionClient()` so a missing provider fails loudly rather than
// silently handing back `null`.
export const VisionClientContext = createContext<VisionClient | null>(null)

export function VisionClientProvider({ children }: { children: ReactNode }) {
  const tenantId = useTenantStore(s => s.tenantId)
  const client = useMemo(() => {
    const live = createVisionClient({
      getToken: getVisionAccessToken,
      getTenant: () => tenantId,
      ...(USING_SHIELD_AUTH
        ? {
            refreshAuth: refreshShieldVisionAccessToken,
            onAuthFailure: expireShieldSession,
          }
        : {}),
    })
    // The shared demo shell supplies /me, tenants, channels, and warehouses.
    // JIT fixtures stay feature-local, but still need that authenticated shell.
    return BULK_UI_DEMO || JIT_UI_DEMO ? withBulkDemoData(live) : live
  }, [tenantId])
  return <VisionClientContext.Provider value={client}>{children}</VisionClientContext.Provider>
}

export function useVisionClient(): VisionClient {
  const client = useContext(VisionClientContext)
  if (!client) throw new Error('useVisionClient outside VisionClientProvider')
  return client
}
